import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { PlusCircledIcon } from '@radix-ui/react-icons';

import { useFlowContext } from '#/providers/FlowContext';
import { useUserContext } from '#/providers/UserContext';

import { Button } from '~/ui/button';

interface Pair {
  key: string;
  value: string;
}

export default function WorkspaceSettingsModal(props: {
  workspaceContext?: Record<string, string>;
  updateWorkspaceContext: (data: any) => Promise<void>;
}) {
  const { workspaceContext = {}, updateWorkspaceContext } = props;

  const { setCanDeleteNode, setIsSettingsShown } = useFlowContext();
  const { userSession, user } = useUserContext();
  const address = userSession?.address;

  const handleClickClose = () => {
    setCanDeleteNode(true);
    setIsSettingsShown(false);
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      pairs: Object.entries(workspaceContext).reduce(
        (pairs, [key, value]) => [...pairs, { key, value }],
        [] as Pair[],
      ),
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'pairs',
  });

  const [isLoading, setIsLoading] = useState(false);
  const onSubmit = async (data: any) => {
    try {
      if (!address) {
        return;
      }

      setIsLoading(true);

      const { pairs } = data;

      const newContext = pairs.reduce((obj: any, pair: any) => {
        obj[pair.key] = pair.value;
        return obj;
      }, {});

      await updateWorkspaceContext({ context: newContext });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="opacity-100 absolute z-[100] h-full w-full pointer-events-auto backdrop-blur-sm flex items-center justify-center p-8">
      <div className="data-[state=open]:animate-overlayShow bg-[url('/grid.svg')] overflow-y-scroll data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] border bg-black bg-opacity-85 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none shadow-widget h-full w-full max-w-[800px] max-h-[500px] rounded-lg flex flex-col items-center justify-start gap-8 p-4">
        <div className="absolute top-4 right-4 z-[110]">
          <label
            tabIndex={0}
            onClick={handleClickClose}
            className={
              'pointer-events-auto btn btn-sm border-0 hover:border-2 hover:border-secondary btn-circle'
            }
          >
            <XMarkIcon className="h-5 w-5" />
          </label>
        </div>
        <div className="menuBody md:py-4 font-semibold text-primary">
          <p>Your Workspace Context Manager</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid md:grid-cols-2 gap-4 w-full py-2 justify-center items-center"
        >
          <label className="text-sm text-primary px-px">Key</label>
          <label className="text-sm text-primary px-px">Value</label>
          {/* register your input into the hook by invoking the "register" function */}
          {fields.map((item, index) => (
            <React.Fragment key={item.id}>
              <div className="flex flex-col">
                <input
                  className="p-2 rounded-sm text-slate-900 dark:text-primary"
                  placeholder="e.g. OPENAI_API_KEY"
                  {...register(`pairs.${index}.key`, { required: true })}
                />
              </div>
              <div className="flex flex-col">
                <input
                  className="p-2 rounded-sm text-slate-900 dark:text-primary"
                  {...register(`pairs.${index}.value`, { required: true })}
                />
              </div>
            </React.Fragment>
          ))}
          <button
            className="text-primary rounded-sm text-sm font-medium py-1 px-3 cursor-pointer flex flex-row items-center space-x-1 md:w-[120px]"
            onClick={() => append({ key: '', value: '' })}
          >
            <PlusCircledIcon className="mr-2" /> <span>Add another</span>
          </button>
          <div />
          {/* className="flex justify-center items-center cursor-pointer my-4 py-3 px-5 m-auto col-span-2 text-primary hover:bg-slate-700 hover:text-primary rounded-md text-sm font-medium" */}
          <Button type="submit">
            {isLoading ? <span className="loading" /> : 'Save Values'}
          </Button>
        </form>
      </div>
    </div>
  );
}
